import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { formatDate } from '../theme';
import { useNotebookColors } from './Notebook';

// The Habits page heading and its completion trend are one piece: the word "Habits" sits still at
// the foot of the heading band (nothing about the data moves it), and the trend picks up right
// after it at the first day's value, anywhere from 0% to 100%, as a smooth ink line over faint
// dashed guides, running to today's value at the end of the band.

export const TREND_RANGES = [
  { days: 7, label: '7d' },
  { days: 30, label: '30d' },
  { days: 90, label: '90d' },
];

// Daily share of habits done. 90 days is smoothed with a trailing 7-day average so the line reads
// as a trend rather than noise.
export const useHabitTrend = (habits, completions, days) =>
  useMemo(() => {
    const smooth = days >= 90 ? 7 : 1;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const raw = [];
    for (let i = days - 1 + (smooth - 1); i >= 0; i--) {
      const date = new Date(today);
      date.setDate(today.getDate() - i);
      const ds = formatDate(date);
      const done = habits.filter(h => completions[h.id]?.[ds]).length;
      raw.push({ date, rate: habits.length ? (done / habits.length) * 100 : 0 });
    }
    const series = raw.slice(smooth - 1).map((point, i) => ({
      ...point,
      rate: raw.slice(i, i + smooth).reduce((sum, p) => sum + p.rate, 0) / smooth,
    }));
    const average = series.length ? Math.round(series.reduce((sum, p) => sum + p.rate, 0) / series.length) : 0;
    return { series, average };
  }, [habits, completions, days]);

const PAD_TOP = 14;
const PAD_BOTTOM = 6;
const LEAD = 20; // air between the title and the start of the line
const GUTTER = 48; // right margin where today's value sits beside the end of the line

// Monotone cubic curve through the points (Fritsch–Carlson): rounded, but it never overshoots a
// day's value, so it can't dip below 0% or rise past 100%.
const f = (n) => n.toFixed(1);
export const smoothPath = (pts) => {
  const n = pts.length;
  if (n === 0) return '';
  if (n === 1) return `M ${f(pts[0].x)} ${f(pts[0].y)}`;
  const dx = [];
  const m = [];
  for (let i = 0; i < n - 1; i++) {
    dx[i] = pts[i + 1].x - pts[i].x;
    m[i] = (pts[i + 1].y - pts[i].y) / (dx[i] || 1);
  }
  const t = [m[0]];
  for (let i = 1; i < n - 1; i++) t[i] = m[i - 1] * m[i] <= 0 ? 0 : (m[i - 1] + m[i]) / 2;
  t[n - 1] = m[n - 2];
  for (let i = 0; i < n - 1; i++) {
    if (m[i] === 0) {
      t[i] = 0;
      t[i + 1] = 0;
      continue;
    }
    const a = t[i] / m[i];
    const b = t[i + 1] / m[i];
    const h = a * a + b * b;
    if (h > 9) {
      const k = 3 / Math.sqrt(h);
      t[i] = k * a * m[i];
      t[i + 1] = k * b * m[i];
    }
  }
  let d = `M ${f(pts[0].x)} ${f(pts[0].y)}`;
  for (let i = 0; i < n - 1; i++) {
    const h = dx[i] / 3;
    d += ` C ${f(pts[i].x + h)} ${f(pts[i].y + h * t[i])} ${f(pts[i + 1].x - h)} ${f(pts[i + 1].y - h * t[i + 1])} ${f(pts[i + 1].x)} ${f(pts[i + 1].y)}`;
  }
  return d;
};

const HabitTrendHead = ({ series, average, rangeDays, meta, controls, actions, compact = false }) => {
  const colors = useNotebookColors();
  const bandRef = useRef(null);
  const titleRef = useRef(null);
  const [box, setBox] = useState({ w: 0, tw: 0 });
  const height = compact ? 124 : 172;

  useLayoutEffect(() => {
    const measure = () =>
      setBox({
        w: bandRef.current?.clientWidth || 0,
        tw: titleRef.current?.offsetWidth || 0,
      });
    measure();
    const observer = new ResizeObserver(measure);
    if (bandRef.current) observer.observe(bandRef.current);
    document.fonts?.ready?.then(measure);
    return () => observer.disconnect();
  }, []);

  const { w, tw } = box;
  const x0 = tw + LEAD; // the first day of the range starts just after the word
  const points = useMemo(() => {
    if (!w || !tw || !series.length) return [];
    const plotW = Math.max(0, w - GUTTER - x0);
    const span = height - PAD_TOP - PAD_BOTTOM;
    return series.map((p, i) => ({
      x: x0 + (series.length > 1 ? (i / (series.length - 1)) * plotW : plotW / 2),
      y: PAD_TOP + (1 - p.rate / 100) * span,
    }));
  }, [series, w, tw, x0, height]);

  const line = smoothPath(points);
  const last = points[points.length - 1];
  const lastRate = series.length ? Math.round(series[series.length - 1].rate) : 0;
  // Faint dashed guides at 0, 25, 50, 75 and 100%, across the plot only (not behind the word)
  const guides = [0, 25, 50, 75, 100].map(rate => PAD_TOP + (1 - rate / 100) * (height - PAD_TOP - PAD_BOTTOM));

  return (
    <div className="pt-5 sm:pt-6">
      <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2 mb-1">
        <p className="font-mono text-xs flex flex-wrap items-center gap-x-3 gap-y-1" style={{ color: colors.textSecondary }}>
          {controls}
          {meta}
        </p>
        {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </div>

      <div
        ref={bandRef}
        className="relative"
        style={{ height }}
        role="img"
        aria-label={`Habits done per day over the last ${rangeDays} days: average ${average}%, today ${lastRate}%`}
      >
        {w > 0 && (
          <svg width={w} height={height} className="absolute inset-0 overflow-visible" aria-hidden="true">
            {tw > 0 && guides.map(y => (
              <line key={y} x1={x0} x2={w - GUTTER} y1={y} y2={y} stroke={colors.border} strokeWidth="1" strokeDasharray="2 6" opacity="0.8" />
            ))}
            <path
              key={`line-${rangeDays}`}
              d={line}
              pathLength="1"
              fill="none"
              stroke={colors.textPrimary}
              strokeWidth="1.75"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="trend-line"
            />
            {last && <circle cx={last.x} cy={last.y} r="3.5" fill={colors.textPrimary} className="trend-end" />}
          </svg>
        )}

        {last && (
          <span
            aria-hidden="true"
            className="absolute font-mono text-[11px] tabular-nums whitespace-nowrap leading-none trend-end"
            style={{ left: last.x + 9, top: last.y - 5, color: colors.textPrimary }}
          >
            {lastRate}%
          </span>
        )}

        <h1
          ref={titleRef}
          className={`absolute left-0 bottom-0 font-semibold leading-[.95] ${compact ? 'text-[44px]' : 'text-[44px] sm:text-[64px]'}`}
          style={{ color: colors.textPrimary, letterSpacing: '-0.045em' }}
        >
          Habits
        </h1>
      </div>
    </div>
  );
};

// The mono range toggle that sits in the meta line
export const TrendRangeToggle = ({ value, onChange }) => {
  const colors = useNotebookColors();
  return (
    <span className="inline-flex items-center gap-2" role="radiogroup" aria-label="Trend range">
      {TREND_RANGES.map(r => (
        <button
          key={r.days}
          role="radio"
          aria-checked={value === r.days}
          onClick={() => onChange(r.days)}
          className="font-mono text-xs underline-offset-4 hover:underline"
          style={{
            color: value === r.days ? colors.textPrimary : colors.textSecondary,
            textDecoration: value === r.days ? 'underline' : undefined,
          }}
        >
          {r.label}
        </button>
      ))}
    </span>
  );
};

// Keeps the chosen range for the session so switching pages doesn't reset it
export const useTrendRange = () => {
  const [days, setDays] = useState(() => {
    try {
      return Number(sessionStorage.getItem('mind_habit_trend')) || 30;
    } catch {
      return 30;
    }
  });
  useEffect(() => {
    try {
      sessionStorage.setItem('mind_habit_trend', String(days));
    } catch {
      // private mode
    }
  }, [days]);
  return [days, setDays];
};

export default HabitTrendHead;
